import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import {
  practiceToday,
  scheduleHoursByDate,
  type AvailabilityRecord,
  type ScheduleDayHours,
} from 'app/lib/aiAgentAvailability'
import type { SchedulingData } from 'app/stores/AIAgentStore'
import { useAIAgentStore } from 'app/stores/StoreContext'
import { logError } from 'lib/logging'

/** Days the business-hours preview covers, today first. */
export const PREVIEW_DAYS = 30

/**
 * The next 30 days of business hours as the practice schedule implies them. `scheduling-data`
 * answers from a day to the end of its month, so the window asks for today and the first of the
 * next month.
 */
export function useScheduleHoursPreview(
  locationId: string,
  enabled: boolean,
  timezone?: string | null,
): { loading: boolean; failed: boolean; days: ScheduleDayHours[] } {
  const agentStore = useAIAgentStore()
  const dates = useMemo(() => {
    const today = moment(practiceToday(timezone))
    return Array.from({ length: PREVIEW_DAYS }, (_, i) => today.clone().add(i, `days`).format(`YYYY-MM-DD`))
  }, [timezone])
  const [responses, setResponses] = useState<SchedulingData[] | `failed`>()

  useEffect(() => {
    if (!enabled) return
    const requestDays = [dates[0], moment(dates[0]).add(1, `month`).startOf(`month`).format(`YYYY-MM-DD`)].filter(
      (day) => day <= dates[dates.length - 1],
    )
    Promise.all(requestDays.map((day) => agentStore.schedulingMonth(locationId, day)))
      .then(setResponses)
      .catch((error: Error) => {
        if (error.name === `AbortError`) return
        logError(`My Agent: could not load the practice schedule`, error)
        setResponses(`failed`)
      })
    return () => agentStore.abortScheduling()
  }, [agentStore, locationId, enabled, dates])

  const days = useMemo(() => {
    if (!responses || responses === `failed`) return []
    const byId = new Map<string, AvailabilityRecord>()
    for (const response of responses) {
      for (const record of response.availabilities ?? []) {
        byId.set(`${record.id}|${record.operatoryId}|${record.specificDate ?? ``}`, record)
      }
    }
    const active = new Set(responses.flatMap((response) => (response.providers ?? []).map((provider) => provider.id)))
    return scheduleHoursByDate([...byId.values()], dates, active)
  }, [responses, dates])

  return { loading: enabled && responses === undefined, failed: responses === `failed`, days }
}
